import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { shiftType } from "services/shiftsService";
import moment from "moment";
import "moment/locale/es";
import { FiEye } from "react-icons/fi";
import Avatar from "components/Avatar";
import Switch from "components/Switch";
import { formatSeconds, calculateDuration } from "../utils/tableUtils";

/**
 * Calcula la duración trabajada entre dos fechas como "1h 07'".
 */
const formatWorkedShort = (start, end) => {
	if (!start || !end) return null;
	const seconds = calculateDuration(start, end);
	if (!seconds || seconds <= 0) return null;
	return formatSeconds(seconds, "h'");
};

/**
 * Formatea una fecha como "Mar 16 Feb 2026" (3 letras día, día mes 3 letras año).
 */
const formatDayLabel = (dateValue) => {
	if (!dateValue) return "";
	const m = moment(dateValue);
	const weekday = m.format("ddd").replace(/\./g, "");
	const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
	return `${cap} ${m.format("D MMM YYYY")}`;
};

const formatHourRange = (start, end) => {
	if (!start) return "";
	const startTxt = moment(start).format("HH:mm");
	const endTxt = end ? moment(end).format("HH:mm") : "--:--";
	return `${startTxt} - ${endTxt}`;
};

const TypeBadge = ({ type }) => {
	if (!type) return null;
	const kind =
		type.name === shiftType.work
			? "work"
			: type.name === "Pausa" || type.work_time === false
			? "pause"
			: "other";
	return (
		<span className={`mobile-attendance-card__type mobile-attendance-card__type--${kind}`}>
			{type.name || "Normal"}
		</span>
	);
};

/**
 * Cabecera del card con datos del empleado y controles (switch aprobar + ver).
 */
const CardEmployeeHeader = ({ item, onToggleApproved }) => {
	const { t } = useTranslation();
	const employee = item.employee || {};
	const ubication = employee.employee_with_ubication?.ubication;
	const funct =
		employee.employee_with_function?.function?.name ||
		employee.position ||
		employee.job_title?.name;
	const fullName = `${employee.name || ""} ${employee.surname || ""}`.trim();

	return (
		<div className="mobile-attendance-card__header">
			<div className="mobile-attendance-card__avatar">
				<Avatar userId={employee.id} name={fullName} alt={fullName} />
			</div>

			<div className="mobile-attendance-card__info">
				<div className="mobile-attendance-card__name-row">
					<span className="mobile-attendance-card__name">{fullName}</span>
					{employee.code && (
						<span className="mobile-attendance-card__code">
							{" "}· ID #{employee.code}
						</span>
					)}
				</div>
				<div className="mobile-attendance-card__meta">
					{funct && (
						<span className="mobile-attendance-card__role">{funct}</span>
					)}
					{funct && ubication?.name && (
						<span className="mobile-attendance-card__dot">·</span>
					)}
					{ubication?.name && (
						<span className="mobile-attendance-card__location">
							{ubication.name}
						</span>
					)}
				</div>
			</div>

			<div className="mobile-attendance-card__actions">
				<Switch
					controlId={`approved-mobile-${item.id}`}
					isOn={item.approved}
					handleToggle={(event) => onToggleApproved(event, item)}
				/>
				<Link
					to={`/times/${employee.id}/employee`}
					className="mobile-attendance-card__eye"
					title={t('attendance.viewTimelog')}
					aria-label={t('attendance.viewTimelog')}
				>
					<FiEye size={18} />
				</Link>
			</div>
		</div>
	);
};

/**
 * Card de fichaje individual: muestra un único periodo.
 */
export const MobileAttendanceIndividualCard = ({ item, onToggleApproved }) => {
	const worked = formatWorkedShort(item.start, item.end);
	const dayValue = item.day || item.start;

	return (
		<div className="mobile-attendance-card">
			<CardEmployeeHeader item={item} onToggleApproved={onToggleApproved} />

			<div className="mobile-attendance-card__period">
				<div className="mobile-attendance-card__period-left">
					<div className="mobile-attendance-card__day">
						{formatDayLabel(dayValue)}
					</div>
					<div className="mobile-attendance-card__hours">
						{formatHourRange(item.start, item.end)}
					</div>
				</div>
				<div className="mobile-attendance-card__period-right">
					{worked && (
						<span className="mobile-attendance-card__duration">{worked}</span>
					)}
					<TypeBadge type={item.type} />
				</div>
			</div>
		</div>
	);
};

/**
 * Card de fichajes agrupados por día: muestra todos los periodos del mismo empleado/día.
 */
export const MobileAttendanceDayCard = ({ group, onToggleApproved }) => {
	const first = group.records[0];
	const headerItem = {
		id: group.id,
		employee: group.employee || first?.employee,
		approved: group.records.every((r) => r.approved),
	};

	const handleGroupToggle = (event) => {
		const nextApproved = event.target.checked;
		group.records.forEach((record) => {
			if (record.approved !== nextApproved) {
				onToggleApproved({ target: { checked: nextApproved } }, record);
			}
		});
	};

	return (
		<div className="mobile-attendance-card mobile-attendance-card--day">
			<CardEmployeeHeader
				item={headerItem}
				onToggleApproved={handleGroupToggle}
			/>

			<div className="mobile-attendance-card__day-periods">
				<div className="mobile-attendance-card__day-title">
					{formatDayLabel(group.day)}
				</div>
				{group.records.map((record) => {
					const worked = formatWorkedShort(record.start, record.end);
					return (
						<div
							key={record.id}
							className="mobile-attendance-card__period mobile-attendance-card__period--row"
						>
							<div className="mobile-attendance-card__period-left">
								<div className="mobile-attendance-card__hours">
									{formatHourRange(record.start, record.end)}
								</div>
							</div>
							<div className="mobile-attendance-card__period-right">
								{worked && (
									<span className="mobile-attendance-card__duration">
										{worked}
									</span>
								)}
								<TypeBadge type={record.type} />
							</div>
						</div>
					);
				})}

				{group.totalSeconds > 0 && (
					<div className="mobile-attendance-card__day-total">
						<span className="mobile-attendance-card__day-total-label">
							Total día
						</span>
						<span className="mobile-attendance-card__day-total-value">
							{formatSeconds(group.totalSeconds, "hm")}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default MobileAttendanceIndividualCard;
